import { Alert, Form, Input, Modal, Typography } from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { formatMoneyValue, type Money } from '@/shared/lib/money'
import { MoneyInput } from '@/shared/ui/MoneyInput'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import { setDebtLimit } from './subscriberApi'

const MAX_LIMIT_MINOR = 100_000_000

interface Values {
  limit: Money | null
  reason?: string
}

/** Abonentning qarz limiti (ADR-057): qarzga sotish shu chegaradan oshmaydi, joriy qarzdan kam bo'lmaydi. */
export function DebtLimitModal({
  subscriberId,
  open,
  balance,
  debtLimit,
  onClose,
}: {
  subscriberId: string
  open: boolean
  balance: Money | null
  debtLimit: Money | null
  onClose: () => void
}) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [form] = Form.useForm<Values>()
  const debt = balance && balance.amount < 0 ? { amount: -balance.amount, currency: balance.currency } : null

  const save = useMutation({
    mutationFn: (values: Values) =>
      setDebtLimit(subscriberId, values.limit?.amount ?? 0, values.reason?.trim() || null),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.subscribers.overview(subscriberId) })
      onClose()
    },
  })

  return (
    <Modal
      open={open}
      title={t('subscriber.debtLimit.title')}
      onCancel={() => {
        save.reset()
        onClose()
      }}
      onOk={() => submitOnValid(form, (values) => save.mutate(values))}
      confirmLoading={save.isPending}
      okText={t('common.save')}
      cancelText={t('common.cancel')}
      destroyOnHidden
    >
      <Typography.Paragraph type="secondary">{t('subscriber.debtLimit.hint')}</Typography.Paragraph>
      {debt ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('subscriber.debtLimit.currentDebt', { debt: formatMoneyValue(debt) })}
        />
      ) : null}
      {save.isError ? (
        <Alert type="error" showIcon style={{ marginBottom: 16 }} message={translateError(save.error, t)} />
      ) : null}
      <Form form={form} layout="vertical" preserve={false} initialValues={{ limit: debtLimit }}>
        <Form.Item
          name="limit"
          label={t('subscriber.debtLimit.limit')}
          rules={[{ required: true, message: t('subscriber.debtLimit.limitRequired') }]}
        >
          <MoneyInput
            currency={debtLimit?.currency ?? balance?.currency}
            minMinor={debt?.amount ?? 0}
            maxMinor={MAX_LIMIT_MINOR}
            style={{ width: '100%' }}
            autoFocus
          />
        </Form.Item>
        <Form.Item name="reason" label={t('subscriber.debtLimit.reason')}>
          <Input.TextArea rows={2} maxLength={200} showCount />
        </Form.Item>
      </Form>
    </Modal>
  )
}
